import { FeatureSwitchKey } from '../models/feature-switches';
import { getFeatureSwitchValue } from '../utils/feature-switch-utils';

export type TFeatureSwitches = {
  [key in FeatureSwitchKey]: string | number | boolean | null;
};

export const FeatureSwitches: TFeatureSwitches = {
  /**
   * Order Details Feature Switches:
   */
  // Added 2025-04-17. Not a true/false switch — it's the remorse-cancel window
  // length in minutes, shown in cancel-flow copy (CancellationDrawer.tsx,
  // CancelItems.tsx). Live policy config, not a rollout flag — keep.
  remorseCancelPeriod: getFeatureSwitchValue('remorseCancelPeriod'),
  proSiteInstructionsMAEnabled: getFeatureSwitchValue(
    'proSiteInstructionsMAEnabled',
  ),
  enableChargedDetailsView: getFeatureSwitchValue(
    'enableChargedDetailsView',
    true,
  ),

  /**
   * Start A Returns Feature Switches:
   * allowSTHUndeliveredReturns - allows STH undelivered returns
   */
  // Added 2024-01-18. Kept: it's turned on in stage for testing, even though
  // config/default.json (the only per-env value maintained in this repo)
  // shows "false" — that's just the local/default fallback, not the live
  // stage value. It's also in PROTECTED_FS_SET, which blocks a prod cookie
  // from forcing it on for a single session — a narrow safety guard, not the
  // reason to keep this switch. Don't remove without confirming with the
  // cancel/returns (MASS) team.
  allowSTHUndeliveredReturns: getFeatureSwitchValue(
    'allowSTHUndeliveredReturns',
    true,
  ),
  allowViewSorisQrCode: getFeatureSwitchValue('allowViewSorisQrCode', true),
  allowSORISCancel: getFeatureSwitchValue('allowSORISCancel', true),
  allowTrackingNumBasedShipments: getFeatureSwitchValue(
    'allowTrackingNumBasedShipments',
    true,
  ),
  // Added 2024-12-19. Not a true/false switch — it's a deployment cutoff
  // timestamp (see isLegacyUPSReturn in utils/returns/helpers-with-fs.ts) used
  // to tell pre- vs post-CXO-migration UPS returns apart so legacy returns
  // (which never got a barcode) don't show the barcode button. Permanent
  // migration marker, not a rollout flag — keep.
  CXO_UPS_DEPLOYMENT_DATE: getFeatureSwitchValue(
    'CXO_UPS_DEPLOYMENT_DATE',
    true,
  ),
  disableStartAReturn: getFeatureSwitchValue('disableStartAReturn', true),
  enableP0ReturnReceiptBarcode: getFeatureSwitchValue(
    'enableP0ReturnReceiptBarcode',
    true,
  ),

  enableMACancelReasonCodes: getFeatureSwitchValue('enableMACancelReasonCodes'),

  enableCPSSearch: getFeatureSwitchValue('enableCPSSearch', true),
  enableBopisOutOfStockRecoverability: getFeatureSwitchValue(
    'enableBopisOutOfStockRecoverability',
  ),
  enableModPTC: getFeatureSwitchValue('enableModPTC', true),
  enableProjectName: getFeatureSwitchValue('enableProjectName', true),
  enableEditDeliveryAddressAppify: getFeatureSwitchValue(
    'enableEditDeliveryAddressAppify',
  ),
  enableDeliveryTrackerReturnChanges: getFeatureSwitchValue(
    'enableDeliveryTrackerReturnChanges',
    true,
  ),
  enableBodfsDfsSoris: getFeatureSwitchValue('enableBodfsDfsSoris', true),
  enableReturnsScheduledPickup: getFeatureSwitchValue(
    'enableReturnsScheduledPickup',
    true,
  ),
  enableBopisOutOfStockSubstitution: getFeatureSwitchValue(
    'enableBopisOutOfStockSubstitution',
  ),
  enableCPSAnnouncementBanner: getFeatureSwitchValue(
    'enableCPSAnnouncementBanner',
  ),
  enableRefundBreakdown: getFeatureSwitchValue('enableRefundBreakdown'),
  enableNativeWallet: getFeatureSwitchValue('enableNativeWallet'),
  enableBopisOutOfStockFfaLiteCall: getFeatureSwitchValue(
    'enableBopisOutOfStockFfaLiteCall',
  ),
  // Guardrail for the OOS "Find a Replacement" (Rec Engine) flow. Cookie-
  // overwriteable (2nd arg true) so it can be toggled per-session like the
  // other BOPIS OOS switches while the feature is still mocked/in progress.
  enableBopisOutOfStockReplacement: getFeatureSwitchValue(
    'enableBopisOutOfStockReplacement',
    true,
  ),
  isBOPISToBODFSEnabled: getFeatureSwitchValue('isBOPISToBODFSEnabled', true),
  enablePTCLineOfCredit: getFeatureSwitchValue('enablePTCLineOfCredit'),
  isSFBLEnabled: getFeatureSwitchValue('isSFBLEnabled'),
  enableLSSCPS25MonthsDefault: getFeatureSwitchValue(
    'enableLSSCPS25MonthsDefault',
    true,
  ),
  checkCancelEligibilityOnFetchOrder: getFeatureSwitchValue(
    'checkCancelEligibilityOnFetchOrder',
  ),
  enableEmbeddedBatteryFee: getFeatureSwitchValue('enablePHEmbeddedBatteryFee'),
  enableCPSOrderLevelSearch: getFeatureSwitchValue('enableCPSOrderLevelSearch'),
  enablePOJOBAndPaymentSearch: getFeatureSwitchValue(
    'enablePOJOBAndPaymentSearch',
  ),
  enableAddItemsFakeDoorFS: getFeatureSwitchValue('enableAddItemsFakeDoorFS'),
  enableAssignProjectDrawer: getFeatureSwitchValue('enableAssignProjectDrawer'),
  enableB2CReorderRecommendations: getFeatureSwitchValue(
    'enableB2CReorderRecommendations',
  ),
  enableTotalAmountAndUserCustomFilters: getFeatureSwitchValue(
    'enableTotalAmountAndUserCustomFilters',
  ),
};
